import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import {
    AuthRedirectDebugDetails,
    AuthRedirectDebugService,
} from '../auth-redirect-debug.service';
import { AUTH_REDIRECT_STATE_KEY, AuthRedirectReason } from '../auth-redirect-reason';
import { AuthEvent, AuthEventType } from '../models/auth-events.model';
import { AuthSessionPolicy } from '../models/auth-session-policy.model';
import { LoggerService } from '../../services/logger.service';
import { AuthLoginService } from './auth-login.service';
import { KeycloakClientService } from './keycloak-client.service';

@Injectable({
    providedIn: 'root',
})
export class AuthSessionService {
    private readonly authEventsSubject = new Subject<AuthEvent>();
    private sessionEndedHandled = false;
    private lastRedirectDebug?: {
        reason: AuthRedirectReason;
        details: AuthRedirectDebugDetails;
    };

    readonly authEvents$ = this.authEventsSubject.asObservable();

    constructor(
        private keycloakClient: KeycloakClientService,
        private authLogin: AuthLoginService,
        private authRedirectDebug: AuthRedirectDebugService,
        private router: Router,
        private logger: LoggerService
    ) {}

    bindKeycloakEvents(): void {
        this.keycloakClient.bindEventHandlers({
            onAuthError: errorData => {
                this.logger.warn('[AuthToken] Keycloak onAuthError', JSON.stringify(errorData));
                this.emit(AuthEventType.OnAuthError, errorData);
            },
            onAuthLogout: () => {
                this.logger.debug(
                    '[AuthToken] Keycloak onAuthLogout (tokens cleared in keycloak-js)',
                    JSON.stringify(this.keycloakClient.getRefreshDiagnostics())
                );
                this.emit(AuthEventType.OnAuthLogout);
            },
            onAuthRefreshSuccess: () => {
                this.logger.debug(
                    '[AuthToken] Keycloak onAuthRefreshSuccess',
                    JSON.stringify(this.keycloakClient.getRefreshDiagnostics())
                );
                this.authRedirectDebug.markRefreshSuccess();
                this.emit(AuthEventType.OnAuthRefreshSuccess);
            },
            onAuthRefreshError: () => {
                this.logger.warn(
                    '[AuthToken] Keycloak onAuthRefreshError',
                    JSON.stringify(this.keycloakClient.getRefreshDiagnostics())
                );
                this.authRedirectDebug.markRefreshError();
                this.emit(AuthEventType.OnAuthRefreshError);
            },
            onAuthSuccess: () => {
                this.sessionEndedHandled = false;
                this.authRedirectDebug.markLogin();
                this.emit(AuthEventType.OnAuthSuccess);
            },
            onTokenExpired: () => {
                this.logger.debug(
                    '[AuthToken] Keycloak onTokenExpired',
                    JSON.stringify(this.keycloakClient.getRefreshDiagnostics())
                );
                this.authRedirectDebug.markTokenExpired();
                this.emit(AuthEventType.OnTokenExpired);
            },
            onActionUpdate: state => {
                this.emit(AuthEventType.OnActionUpdate, state);
            },
            onReady: authenticated => {
                this.emit(AuthEventType.OnReady, authenticated);
            },
        });
    }

    isAuthenticated(): boolean {
        return this.keycloakClient.isLoggedIn();
    }

    isInitialized(): boolean {
        return this.keycloakClient.isInitialized();
    }

    handleSessionEnded(
        reason: AuthRedirectReason,
        policy: AuthSessionPolicy = AuthSessionPolicy.ReLogin,
        details: AuthRedirectDebugDetails = {}
    ): void {
        if (!this.keycloakClient.isInitialized() || this.sessionEndedHandled) {
            return;
        }

        this.sessionEndedHandled = true;
        this.lastRedirectDebug = { reason, details };
        this.authRedirectDebug.log(reason, details);

        if (policy === AuthSessionPolicy.FullLogout) {
            void this.router.navigateByUrl('/logout', {
                state: { [AUTH_REDIRECT_STATE_KEY]: reason },
            });
            return;
        }

        this.authLogin.redirectToLogin(this.lastRedirectDebug);
    }

    consumeLastRedirectDebug(): typeof this.lastRedirectDebug {
        const debug = this.lastRedirectDebug;
        this.lastRedirectDebug = undefined;
        return debug;
    }

    private emit(type: AuthEventType, args?: unknown): void {
        this.authEventsSubject.next({ type, args });
    }
}
